import React, { useMemo, useState } from 'react';
import './Target.css';

const Target = ({ criterion }) => {
    // Размеры мишени
    const TARGET_SIZE = 320;
    const CENTER = TARGET_SIZE / 2;
    
    // Радиусы 4 кругов мишени
    const RADII = [40, 80, 120, 160];
    
    // Состояние для подсветки связей
    const [hoveredUser, setHoveredUser] = useState(null);
    
    // Подготовка данных пользователей
    const { users, userPositions } = useMemo(() => {
        const result = {
            users: [],
            userPositions: {}
        };
        
        if (!criterion?.users) return result;
        
        const usersArray = Object.entries(criterion.users).map(([id, data]) => ({
            id,
            carma: data.carma
        }));

        // Находим минимальное и максимальное значение кармы
        const carmaValues = usersArray.map(u => u.carma);
        const minCarma = Math.min(...carmaValues);
        const maxCarma = Math.max(...carmaValues);
        
        // Нормализуем карму в диапазон 0-1
        const normalize = (value) => {
            if (maxCarma === minCarma) return 0.5;
            return (value - minCarma) / (maxCarma - minCarma);
        };

        // Сортируем пользователей по карме (от высокой к низкой)
        const sortedUsers = usersArray
            .map(user => ({
                ...user,
                normalized: normalize(user.carma)
            }))
            .sort((a, b) => b.carma - a.carma);
        
        // Распределяем пользователей по мишени
        const positionedUsers = sortedUsers.map((user, index) => {
            // Рассчитываем расстояние от центра (0-1)
            const distance = 1 - user.normalized;
            
            // Рассчитываем радиус (20px - минимальный отступ от центра)
            const radius = 20 + 140 * distance;
            
            // Распределяем по золотому углу (137.5° в радианах)
            const angle = index * 2.39996;
            
            // Рассчитываем координаты
            const x = CENTER + radius * Math.cos(angle);
            const y = CENTER + radius * Math.sin(angle);
            
            // Размер кружка зависит от кармы
            const size = 30 + 10 * user.normalized;
            
            return {
                ...user,
                x,
                y,
                size
            };
        });
        
        // Создаем объект для быстрого доступа к позициям
        const positions = {};
        positionedUsers.forEach(user => {
            positions[user.id] = { x: user.x, y: user.y };
        });
        
        return {
            users: positionedUsers,
            userPositions: positions
        };
    }, [criterion]);

    // Подготовка связей между пользователями
    const connections = useMemo(() => {
        const result = [];
        if (!criterion || !userPositions) return result;

        // Обрабатываем положительные связи
        criterion.pos_mutual.forEach(pair => {
            const [from, to] = pair.split('-');
            if (userPositions[from] && userPositions[to]) {
                result.push({
                    from: userPositions[from],
                    to: userPositions[to],
                    type: 'positive',
                    participants: [from, to]
                });
            }
        });

        // Обрабатываем отрицательные связи
        criterion.neg_mutual.forEach(pair => {
            const [from, to] = pair.split('-');
            if (userPositions[from] && userPositions[to]) {
                result.push({
                    from: userPositions[from],
                    to: userPositions[to],
                    type: 'negative',
                    participants: [from, to]
                });
            }
        });

        return result;
    }, [criterion, userPositions]);

    // Проверка, связана ли линия с пользователем
    const isConnectionRelated = (connection) => {
        if (!hoveredUser) return false;
        return connection.participants.includes(hoveredUser.id);
    };

    return (
        <div className="target-card">
            <h3 className="target-title">{criterion.criteria}</h3>
            
            <div className="target" style={{ width: TARGET_SIZE, height: TARGET_SIZE }}>
                {/* Рисуем 4 круга мишени */}
                {RADII.map((radius, index) => (
                    <div
                        key={index}
                        className={`target-circle circle-${index}`}
                        style={{
                            width: radius * 2,
                            height: radius * 2,
                            left: CENTER - radius,
                            top: CENTER - radius
                        }}
                    />
                ))}
                
                {/* Рисуем связи между пользователями */}
                <svg className="connections-layer" width={TARGET_SIZE} height={TARGET_SIZE}>
                    {connections.map((conn, idx) => (
                        <line
                            key={idx}
                            x1={conn.from.x}
                            y1={conn.from.y}
                            x2={conn.to.x}
                            y2={conn.to.y}
                            className={`connection ${conn.type} ${isConnectionRelated(conn) ? 'highlighted' : ''}`}
                            strokeWidth="1"
                        />
                    ))}
                </svg>
                
                {/* Рисуем пользователей */}
                {users.map(user => (
                    <div
                        key={user.id}
                        className={`user-circle ${user.carma >= 0 ? 'positive' : 'negative'} ${user.carma === 0 ? 'zero' : ''} ${hoveredUser?.id === user.id ? 'highlighted' : ''}`}
                        style={{
                            width: user.size,
                            height: user.size,
                            left: user.x,
                            top: user.y,
                            fontSize: Math.max(12, 14 + 4 * user.normalized),
                            transform: 'translate(-50%, -50%)'
                        }}
                        title={`Участник ${user.id}, Карма: ${user.carma}`}
                        onMouseEnter={() => setHoveredUser(user)}
                        onMouseLeave={() => setHoveredUser(null)}
                    >
                        {user.id}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Target;